import styled from 'styled-components';

const ink = '#20201d';
const muted = '#6f7069';
const paper = '#f5f4ef';
const card = '#fffdf9';
const border = '#deded5';
const accent = '#b65d3b';
const lift = '0 14px 32px rgba(32, 32, 29, 0.1)';
const motion = '0.3s cubic-bezier(0.4, 0, 0.2, 1)';

export const TemplateWrapper = styled.div`
  width: 100%;
  box-sizing: border-box;
  min-height: 100vh;
  color: ${ink};
  background: radial-gradient(circle at 88% 4%, rgba(182, 93, 59, 0.08), transparent 24rem), ${paper};
  font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  line-height: 1.6;
`;

export const HeroSection = styled.header`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: clamp(5rem, 12vw, 9rem) clamp(1.5rem, 5vw, 5rem) clamp(4rem, 8vw, 6rem);
  text-align: center;
  isolation: isolate;

  &::before { content: 'PORTFOLIO'; position: absolute; top: 2rem; right: 2rem; color: ${accent}; font-size: 0.68rem; font-weight: 800; letter-spacing: 0.2em; }
  &::after { content: ''; position: absolute; z-index: -1; top: 3.5rem; left: 50%; width: min(42vw, 24rem); aspect-ratio: 1; border: 1px solid rgba(182, 93, 59, 0.14); border-radius: 50%; transform: translateX(-50%); }
  @media (max-width: 600px) { &::before { right: 1.5rem; } }
`;

export const Avatar = styled.img`
  width: clamp(96px, 16vw, 128px);
  height: clamp(96px, 16vw, 128px);
  margin-bottom: 2rem;
  border: 1px solid ${ink};
  border-radius: 50%;
  object-fit: cover;
  box-shadow: 10px 10px 0 ${accent};
`;

export const Name = styled.h1`
  max-width: 800px;
  margin: 0 0 1rem;
  color: ${ink};
  font-family: Georgia, 'Times New Roman', serif;
  font-size: clamp(2.8rem, 8vw, 6.5rem);
  font-weight: 400;
  letter-spacing: -0.06em;
  line-height: 0.96;
  overflow-wrap: anywhere;
`;

export const Bio = styled.p`
  max-width: 620px;
  margin: 0 0 2rem;
  color: ${muted};
  font-family: Georgia, 'Times New Roman', serif;
  font-size: clamp(1.05rem, 2vw, 1.3rem);
  line-height: 1.7;
  white-space: pre-line;
`;

export const LinksRow = styled.nav`
  display: flex;
  flex-wrap: wrap;
  gap: 0.7rem;
  align-items: center;
  justify-content: center;
  max-width: 720px;
`;

export const LinkItem = styled.a`
  display: inline-flex;
  gap: 0.45rem;
  align-items: center;
  padding: 0.65rem 0.9rem;
  border: 1px solid ${border};
  border-radius: 999px;
  color: ${ink};
  background: ${card};
  font-size: 0.84rem;
  font-weight: 650;
  text-decoration: none;
  transition: transform ${motion}, border-color ${motion}, color ${motion};
  &:hover, &:focus-visible { border-color: ${accent}; color: ${accent}; outline: none; transform: translateY(-2px); }
`;

export const Section = styled.section`
  width: calc(100% - clamp(2rem, 8vw, 8rem));
  max-width: 1180px;
  margin: 0 auto;
  padding: clamp(3.5rem, 7vw, 4.5rem) 0;
  border-top: 1px solid ${border};
`;

export const SectionTitle = styled.h2`
  display: flex;
  gap: 0.9rem;
  align-items: center;
  margin: 0 0 2.3rem;
  color: ${ink};
  font-size: 0.78rem;
  font-weight: 800;
  letter-spacing: 0.18em;
  line-height: 1.2;
  text-transform: uppercase;
  &::before { content: ''; display: block; width: 2.5rem; height: 2px; background: ${accent}; }
`;

export const EntryCard = styled.article`
  margin-bottom: 1.6rem;
  padding: clamp(1.25rem, 3vw, 1.7rem) clamp(1.2rem, 3vw, 1.8rem);
  border: 1px solid ${border};
  border-radius: 12px;
  background: ${card};
  box-shadow: 0 4px 20px rgba(32, 32, 29, 0.05);
  transition: transform ${motion}, box-shadow ${motion}, border-color ${motion};
  &:hover { border-color: rgba(182, 93, 59, 0.45); box-shadow: ${lift}; transform: translateY(-4px); }
  &:last-child { margin-bottom: 0; }
`;

export const EntryHeader = styled.div`
  display: flex;
  gap: 1.5rem;
  align-items: flex-start;
  justify-content: space-between;
  @media (max-width: 600px) { flex-direction: column; gap: 0.6rem; }
`;

export const EntryTitle = styled.h3`
  margin: 0;
  color: ${ink};
  font-family: Georgia, 'Times New Roman', serif;
  font-size: 1.25rem;
  font-weight: 400;
  line-height: 1.25;
`;

export const EntrySubtitle = styled.p`margin: 0.35rem 0 0; color: ${muted}; font-size: 0.9rem; font-weight: 600;`;
export const EntryDate = styled.time`flex-shrink: 0; color: ${accent}; font-size: 0.75rem; font-weight: 800; letter-spacing: 0.08em; text-transform: uppercase;`;
export const EntryDescription = styled.p`max-width: 720px; margin: 1rem 0 0; color: ${muted}; font-family: Georgia, 'Times New Roman', serif; font-size: 1rem; line-height: 1.75;`;
export const SkillsGrid = styled.div`display: flex; flex-wrap: wrap; gap: 0.8rem;`;
export const SkillBadge = styled.button`
  padding: 0.7rem 1rem;
  border: 1px solid ${border};
  border-radius: 999px;
  color: ${ink};
  background: ${card};
  font: inherit;
  font-size: 0.86rem;
  font-weight: 650;
  cursor: pointer;
  transition: transform ${motion}, border-color ${motion}, color ${motion}, box-shadow ${motion};

  &:hover,
  &:focus-visible {
    border-color: ${accent};
    color: ${accent};
    outline: none;
    box-shadow: 0 6px 16px rgba(182, 93, 59, 0.14);
    transform: translateY(-2px);
  }
`;

export const ProjectsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 270px), 1fr));
  gap: 2rem;
`;

export const ProjectCard = styled.article`
  overflow: hidden;
  min-width: 0;
  border: 1px solid ${border};
  border-radius: 12px;
  background: ${card};
  box-shadow: 0 4px 20px rgba(32, 32, 29, 0.05);
  cursor: pointer;
  transition: transform ${motion}, box-shadow ${motion};
  &:hover { box-shadow: ${lift}; transform: translateY(-6px); }
`;

export const ProjectImage = styled.img`display: block; width: 100%; height: clamp(150px, 22vw, 190px); object-fit: cover; filter: saturate(0.85);`;
export const ProjectImagePlaceholder = styled.div`display: flex; align-items: flex-end; width: 100%; height: clamp(150px, 22vw, 190px); padding: 1.5rem; color: ${card}; background: linear-gradient(135deg, ${ink}, ${accent}); font-family: Georgia, 'Times New Roman', serif; font-size: 1.4rem; line-height: 1.1; overflow-wrap: anywhere;`;
export const ProjectBody = styled.div`padding: clamp(1.2rem, 3vw, 1.6rem);`;
export const ProjectTitle = styled.h3`margin: 0 0 0.7rem; color: ${ink}; font-family: Georgia, 'Times New Roman', serif; font-size: 1.35rem; font-weight: 400; overflow-wrap: anywhere;`;
export const ProjectDescription = styled.p`margin: 0 0 1.25rem; color: ${muted}; font-size: 0.9rem; line-height: 1.7;`;
export const ProjectLinks = styled.div`display: flex; flex-wrap: wrap; gap: 1rem;`;
export const ProjectLink = styled.a`display: inline-flex; gap: 0.4rem; align-items: center; color: ${accent}; font-size: 0.82rem; font-weight: 800; text-decoration: none; &:hover, &:focus-visible { text-decoration: underline; text-underline-offset: 4px; }`;
export const CertLink = styled.a`display: inline-block; margin-top: 1rem; color: ${accent}; font-size: 0.82rem; font-weight: 800; text-decoration: none; &:hover, &:focus-visible { text-decoration: underline; text-underline-offset: 4px; }`;
export const ContactSectionWrapper = styled.div`
  width: calc(100% - clamp(2rem, 8vw, 8rem));
  max-width: 1180px;
  margin: 0 auto;
  padding-bottom: clamp(3.5rem, 7vw, 5rem);
`;
